import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Sparkles, ArrowRight, X } from 'lucide-react';
import { useAuth } from '../../Hooks/useAuth';

export const FloatingRegisterCTA: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Ne s'affiche PAS par défaut : apparaît uniquement lorsque l'utilisateur non connecté tente une action (saisie, clic)
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    if (isAuthenticated || isDismissed) return;

    // Détecte quand l'invité clique ou commence à taper dans un champ de saisie
    const handleInputOrFocus = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable ||
          target.closest('form') ||
          target.closest('[role="combobox"]'))
      ) {
        setIsVisible(true);
        window.dispatchEvent(new CustomEvent('docarn-register-cta-visible'));
      }
    };

    // Détecte un déclenchement explicite d'action requérant une connexion
    const handleGuestAction = () => {
      setIsVisible(true);
      window.dispatchEvent(new CustomEvent('docarn-register-cta-visible'));
    };

    window.addEventListener('focusin', handleInputOrFocus, { passive: true });
    window.addEventListener('input', handleInputOrFocus, { passive: true });
    window.addEventListener('docarn-guest-action-attempted', handleGuestAction);

    return () => {
      window.removeEventListener('focusin', handleInputOrFocus);
      window.removeEventListener('input', handleInputOrFocus);
      window.removeEventListener('docarn-guest-action-attempted', handleGuestAction);
    };
  }, [isAuthenticated, isDismissed]);

  // Ne pas afficher sur /register, /auth, ou si déjà connecté, fermé ou non déclenché
  if (
    isAuthenticated ||
    isDismissed ||
    !isVisible ||
    location.pathname.startsWith('/register') ||
    location.pathname.startsWith('/auth')
  ) {
    return null;
  }

  const handleClose = () => {
    setIsDismissed(true);
    setIsVisible(false);
    window.dispatchEvent(new CustomEvent('docarn-register-cta-dismissed'));
  };

  return (
    <div className="fixed bottom-6 right-4 sm:right-6 z-40 animate-fade-in">
      <div className="bg-gradient-to-r from-violet-900 to-indigo-900 text-white rounded-2xl p-3.5 shadow-xl border border-violet-700/40 max-w-xs sm:max-w-sm flex items-start gap-2.5 backdrop-blur-md">
        <div className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center shrink-0 mt-0.5 text-amber-300">
          <Sparkles className="w-3.5 h-3.5" />
        </div>
        
        <div className="flex-1 pr-1">
          <h4 className="text-[11px] font-bold text-white tracking-wide">
            Accès Juridique Découverte
          </h4>
          <p className="text-[10px] text-violet-200 mt-0.5 leading-snug">
            Créez votre compte gratuit pour consulter les textes complets et sauvegarder vos recherches.
          </p>
          <div className="mt-2.5 flex items-center gap-2">
            <button
              onClick={() => navigate('/register')}
              className="px-2.5 py-1 bg-white hover:bg-violet-50 text-violet-900 text-[11px] font-bold rounded-lg shadow-sm transition-all flex items-center gap-1 cursor-pointer"
            >
              <span>Créer un compte</span>
              <ArrowRight className="w-2.5 h-2.5" />
            </button>
          </div>
        </div>

        <button
          onClick={handleClose}
          className="text-white/60 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
          title="Fermer"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};
